import React, { useMemo, useState } from 'react';
import { Avatar, Modal } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import { Booking, useCreateBooking, useUpdateBooking } from '../../hooks/api/booking';
import { useGetUsers } from '../../hooks/api/user';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import './participantsList.scss';

interface MobileParticipantsListProps {
  bookings: Booking[];
  isOpen: boolean;
  closeModal: () => void;
  lessonId: number;
  maxSeats?: number;
}

const BELT_META: Record<string, { label: string; color: string }> = {
  white: { label: 'Blanco', color: '#e0e0e0' },
  blue: { label: 'Azul', color: '#1565c0' },
  purple: { label: 'Morado', color: '#6a1b9a' },
  brown: { label: 'Marrón', color: '#4e342e' },
  black: { label: 'Negro', color: '#111111' },
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente',
  declined: 'Rechazada',
};

export default function MobileParticipantsList({
  closeModal,
  bookings,
  isOpen,
  lessonId,
  maxSeats,
}: MobileParticipantsListProps) {
  const { isInstructor } = useCurrentUser();
  const createBookingMutation = useCreateBooking();
  const updateBookingMutation = useUpdateBooking();
  const [newBookingUser, setNewBookingUser] = useState('');

  const getUsersQuery = useGetUsers({ hasPlan: true });
  const users = useMemo(() => getUsersQuery.data ?? [], [getUsersQuery.data]);
  const students = useMemo(
    () =>
      users.filter(
        (user) => !user.instructor && !bookings.some((booking) => booking.user.id === user.id),
      ),
    [users, bookings],
  );

  const addAcceptedBooking = () => {
    createBookingMutation.mutate(
      {
        lessonId,
        userId: parseInt(newBookingUser),
        status: 'approved',
      },
      { onSuccess: () => setNewBookingUser('') },
    );
  };

  const updateBooking = (bookingId: number, status: 'approved' | 'declined') => {
    updateBookingMutation.mutate({ bookingId, changeset: { status } });
  };

  const renderStatus = (booking: Booking) => {
    // Booking status is an instructor concern — members just see the roster.
    if (!isInstructor) return null;

    if (booking.status === 'pending') {
      return (
        <div className="pl-status">
          <button
            type="button"
            className="pl-act pl-act--ok"
            aria-label="Aceptar"
            onClick={() => updateBooking(booking.id, 'approved')}
          >
            <CheckIcon />
          </button>
          <button
            type="button"
            className="pl-act pl-act--no"
            aria-label="Rechazar"
            onClick={() => updateBooking(booking.id, 'declined')}
          >
            <ClearIcon />
          </button>
        </div>
      );
    }

    if (booking.status === 'approved') return null;

    return <span className={`pl-badge pl-badge--${booking.status}`}>{STATUS_LABEL[booking.status]}</span>;
  };

  return (
    <Modal open={isOpen} onClose={closeModal}>
      <div className="pl-sheet">
        <span className="pl-grip" />

        <div className="pl-head">
          <h2 className="pl-head__title">Participantes</h2>
          <span className="pl-head__count">
            {bookings.length}
            {maxSeats ? `/${maxSeats}` : ''}
          </span>
          <button type="button" className="pl-close" aria-label="Cerrar" onClick={closeModal}>
            <CloseIcon />
          </button>
        </div>

        {isInstructor && (
          <div className="pl-add">
            <select
              value={newBookingUser}
              onChange={(event) => setNewBookingUser(event.target.value)}
              aria-label="Añadir alumno"
            >
              <option value="" disabled>
                Añadir alumno…
              </option>
              {students.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.nickName ?? `${user.firstName} ${user.lastName}`}
                </option>
              ))}
            </select>
            <button type="button" disabled={newBookingUser === ''} onClick={addAcceptedBooking}>
              Añadir
            </button>
          </div>
        )}

        <div className="pl-list">
          {bookings.length === 0 ? (
            <p className="pl-empty">Aún no hay reservas</p>
          ) : (
            bookings.map((booking) => {
              const belt = BELT_META[booking.user.belt] ?? BELT_META.white;
              const name =
                booking.user.nickName ??
                `${booking.user.firstName} ${booking.user.lastName}`.trim();
              return (
                <div className="pl-item" key={booking.userId}>
                  <span className="pl-avatar" style={{ ['--belt' as string]: belt.color }}>
                    <Avatar src={booking.user.image} alt={name} sx={{ width: 36, height: 36 }} />
                  </span>
                  <div className="pl-item__info">
                    <span className="pl-item__name">{name}</span>
                    <span className="pl-item__belt">
                      <span className="pl-item__beltdot" style={{ backgroundColor: belt.color }} />
                      {belt.label}
                    </span>
                  </div>
                  {renderStatus(booking)}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}
